import React, {Fragment, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {connect} from 'react-redux';
import {hasValue, getReportHref} from '../../utils';
import {ReviewActions, ReviewDialogActions, UploadActions} from '../../actions';
import {replyDefaultCount, REVIEW_TYPES} from '../../constants';
import {Rate, Icon, ReadMoreText, getMessage, Message} from '../index';
import ReplyItem from './ReplyItem';
import ReviewSubTitle from './ReviewSubTitle';
import ReviewImages from './ReviewImages';

const mapStateToProps = state => {
    const messages = state.get('localization').get('messageProperties');
    const {data = {}} = state
        .get('asset')
        .get('data')
        .toJS();
    const {accountInfo, isLoggedIn, isPostingAllowed} = state.get('user').toJS();
    const isLauncher = state.get('config').get('isLauncher');
    const globalBase = state.get('config').get('globalBase');
    const isImageFeatureEnabled = state.get('config').get('isImageFeatureEnabled');
    const {isLoadingReply, isLoadingReview, activeId} = state.get('review').toJS();
    const {seller = {}, id} = data;
    return {
        messages,
        sellerId: seller.owner,
        offerId: id,
        accountInfo,
        isLoggedIn,
        isPostingAllowed,
        isLauncher,
        isLoadingReply,
        isLoadingReview,
        activeId,
        globalBase,
        isImageFeatureEnabled
    };
};

const mapDispatchToProps = dispatch => ({
    editReview: model => dispatch(ReviewActions.editReview(model)),
    deleteReview: model => dispatch(ReviewActions.deleteReview(model)),
    addReply: model => dispatch(ReviewActions.addReply(model)),
    showMoreReplies: model => dispatch(ReviewActions.showMoreReplies(model)),
    getReviewReplies: model => dispatch(ReviewActions.getReviewReplies(model)),
    uploadImages: model => dispatch(UploadActions.uploadQueue(model)),
    setDialogOptions: model => dispatch(ReviewDialogActions.setOptions(model)),
    mergeDialogOptions: model => dispatch(ReviewDialogActions.mergeOptions(model)),
    hideDialog: () => dispatch(ReviewDialogActions.hide(true))
});

const ReviewItem = props => {
    const prefixCls = 'review-item';
    const {
        className,
        type,
        data = {},
        messages,
        sellerId,
        accountInfo,
        isLauncher,
        isLoadingReply,
        isLoadingReview,
        activeId,
        getReviewReplies,
        showMoreReplies,
        offerId,
        fullScreenDispatch,
        isShowReadMore = true,
        isImageFeatureEnabled
    } = props;
    const {
        id,
        isCurrentUser,
        rating,
        title,
        replyNum,
        content,
        images = [],
        replies,
        replyPaging,
        publisherReply = {},
        bestReply,
        defaultReply,
        identityId,
        displayed
    } = data;
    const classNames = cn(prefixCls, className);
    const [showReplies, toggleReplies] = useState(false);
    const [hideReview, showReviewAnyway] = useState(!displayed);

    useEffect(() => {
        if (isLoadingReview && activeId === id) {
            toggleReplies(false);
        }
    }, [isLoadingReview]);

    const handleShowReplies = async () => {
        if (!showReplies) {
            await getReviewReplies({topicId: id, count: replyDefaultCount, type, offerId});
        }
        toggleReplies(!showReplies);
    };

    const renderTools = () => {
        let loadReplyEle = null;
        if (isLoadingReply && activeId === id) {
            loadReplyEle = (
                <li>
                    <div className="loading" />
                </li>
            );
        } else if (
            replyNum > 2 ||
            (replyNum === 1 &&
                !hasValue(bestReply) &&
                !hasValue(publisherReply) &&
                !hasValue(defaultReply)) ||
            (replyNum === 2 &&
                !(hasValue(bestReply) && hasValue(publisherReply) && !publisherReply.isBest))
        ) {
            //hidden when the replyNum = 1 and the reply is publisherReply/bestReply
            let showReplyCode;
            if (showReplies) {
                showReplyCode = 'messages.com.epicgames.plugin.store.asset.replies.hide';
            } else if (replyNum === 1) {
                showReplyCode = 'messages.com.epicgames.plugin.store.asset.replies.show.single';
            } else {
                showReplyCode = 'messages.com.epicgames.plugin.store.asset.replies.show';
            }
            loadReplyEle = (
                <li onClick={handleShowReplies}>
                    <span>{getMessage(messages, showReplyCode, [replyNum])}</span>
                </li>
            );
        }
        return (
            <Fragment>
                {!isCurrentUser && (
                    <li title="Report">
                        {/* eslint-disable-next-line react/jsx-no-target-blank */}
                        <a
                            href={getReportHref(
                                type === REVIEW_TYPES.REVIEW ? 'review' : 'question',
                                id,
                                type === REVIEW_TYPES.REVIEW ? 'uem_review' : 'uem_question'
                            )}
                            target="_blank">
                            <Icon name="flag" />
                        </a>
                    </li>
                )}
                {loadReplyEle}
            </Fragment>
        );
    };

    const onLoadMoreReplies = () => {
        const {start, count} = replyPaging;
        showMoreReplies({start: start + count, count, topicId: id, type, offerId});
    };

    const getReplyTitle = (fromBest, fromPublisher, isLatest) => {
        if (fromBest) {
            return getMessage(
                messages,
                `messages.com.epicgames.plugin.store.asset.replies${
                    fromPublisher ? '.publisher' : ''
                }.best`
            );
        }
        if (fromPublisher && isLatest) {
            return getMessage(
                messages,
                `messages.com.epicgames.plugin.store.asset.${type}.replies.publisher.latest`
            );
        } else if (fromPublisher && !isLatest) {
            return getMessage(
                messages,
                `messages.com.epicgames.plugin.store.asset.${type}.replies.publisher`
            );
        }
        return null;
    };

    const renderReplies = () => {
        const showReplyList =
            (!isLoadingReply || (isLoadingReply && activeId !== id)) &&
            showReplies &&
            replies &&
            replies.length;
        if (showReplyList) {
            const {start, count, total} = replyPaging;
            const noMoreReplies = start + count >= total;
            return (
                <div className={`${prefixCls}__replies`}>
                    {replies.map((it, idx) => {
                        return (
                            <ReplyItem
                                type={type}
                                key={`reply-item-list-${idx}`}
                                topicIdentityId={identityId}
                                isPublisher={it.identityId === sellerId}
                                data={{
                                    ...it,
                                    title: getReplyTitle(
                                        it.isBest,
                                        it.identityId === sellerId,
                                        hasValue(publisherReply) && it.id === publisherReply.id
                                    )
                                }}
                            />
                        );
                    })}
                    {!noMoreReplies && (
                        <div className={`${prefixCls}__replies__more`}>
                            <span onClick={() => onLoadMoreReplies()}>
                                {getMessage(
                                    messages,
                                    'messages.com.epicgames.plugin.store.message.more'
                                )}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        const displayReply = [];
        if (hasValue(bestReply)) {
            displayReply.push({
                ...bestReply,
                title: getReplyTitle(true, bestReply.identityId === sellerId)
            });
        }
        if (hasValue(publisherReply) && !publisherReply.isBest) {
            displayReply.push({...publisherReply, title: getReplyTitle(false, true, true)});
        }
        if (hasValue(defaultReply)) {
            displayReply.push(defaultReply);
        }
        if (displayReply.length) {
            return (
                <div className={`${prefixCls}__replies`}>
                    {displayReply
                        .filter(reply => reply.displayed)
                        .map((it, idx) => (
                            <ReplyItem
                                key={`${idx}`}
                                className={`${prefixCls}__best-reply`}
                                type={type}
                                data={it}
                                isPublisher={it.identityId === sellerId}
                                topicIdentityId={identityId}
                            />
                        ))}
                </div>
            );
        }
        return null;
    };

    const handleFullScreen = currentIdx => {
        fullScreenDispatch({
            payload: {
                isVisible: true,
                data: images,
                currentIdx
            }
        });
    };

    const renderContent = () => {
        if (hideReview) {
            const isSelf = accountInfo.id === identityId;
            const message = getMessage(
                messages,
                'messages.com.epicgames.plugin.store.asset.reivew_support_for_help'
            );
            return (
                <Fragment>
                    <div className={`${prefixCls}__title`}>
                        <Message code="messages.com.epicgames.plugin.store.asset.review_on_hide" />
                    </div>
                    {isSelf && (
                        <div
                            className={`${prefixCls}__content self_warning`}
                            dangerouslySetInnerHTML={{__html: message}}
                        />
                    )}
                    <ul className={`${prefixCls}__tools`}>
                        <li className="button-show_anyway" onClick={() => showReviewAnyway(false)}>
                            {getMessage(
                                messages,
                                'messages.com.epicgames.plugin.store.asset.review_show_anyway'
                            )}
                        </li>
                    </ul>
                </Fragment>
            );
        }
        return (
            <Fragment>
                <div className={`${prefixCls}__header`}>
                    <div className={`${prefixCls}__title`}>
                        {hasValue(rating) && (
                            <Rate readonly initialRating={rating} isLauncher={isLauncher} />
                        )}
                        <h4>{title}</h4>
                        <ReviewSubTitle data={data} />
                    </div>
                </div>
                {isShowReadMore ? (
                    <ReadMoreText
                        className={`${prefixCls}__content rich-text-content`}
                        text={content}
                    />
                ) : (
                    <div dangerouslySetInnerHTML={{__html: content}} />
                )}
                {isImageFeatureEnabled && images.length > 0 && (
                    <ReviewImages
                        className={`${prefixCls}__images`}
                        images={images}
                        fullScreenDispatch={handleFullScreen}
                    />
                )}
                <ul className={`${prefixCls}__tools`}>{renderTools()}</ul>
                {renderReplies()}
            </Fragment>
        );
    };

    return (
        <div className={classNames}>
            {!!isLoadingReview && activeId === id ? <div className="loading" /> : renderContent()}
        </div>
    );
};

ReviewItem.propTypes = {
    className: PropTypes.string,
    offerId: PropTypes.string,
    type: PropTypes.oneOf(['reviews', 'questions']),
    data: PropTypes.object,
    messages: PropTypes.object,
    sellerId: PropTypes.string,
    isLoadingReply: PropTypes.bool,
    isLoadingReview: PropTypes.bool,
    activeId: PropTypes.string,
    accountInfo: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    isPostingAllowed: PropTypes.bool,
    isLauncher: PropTypes.bool,
    editReview: PropTypes.func,
    deleteReview: PropTypes.func,
    voteReview: PropTypes.func,
    addReply: PropTypes.func,
    showMoreReplies: PropTypes.func,
    getReviewReplies: PropTypes.func,
    setDialogOptions: PropTypes.func,
    mergeDialogOptions: PropTypes.func,
    hideDialog: PropTypes.func,
    uploadImages: PropTypes.func,
    fullScreenDispatch: PropTypes.func,
    isShowReadMore: PropTypes.bool,
    globalBase: PropTypes.string,
    isVoting: PropTypes.bool,
    isImageFeatureEnabled: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewItem);
